import { Button, Empty, message, Typography } from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { loader } from "components/QueryWrapper";
import DashboardLayout from "layout/DashboardLayout";
import { useParams } from "react-router-dom";
import { SafetyOutlined } from "@ant-design/icons";
import BehavioralCharts from "./components/BehavioralCharts";
import CashFlowPage from "./components/CashFlow";
import ProbabilityDefaultAnalysis from "./components/ProbabilityDefaultAnalysis";
import OverView from "./components/OverView";

const { Title, Text } = Typography;

const NewIntelligence = () => {
  const { app_key } = useParams();
  const fullApplication = ApiService.application.useGetByAppKey(app_key);
  const intelligenceData = ApiService.application.useGetIntelligenceNumbers(app_key);
  const getDebtorSummary = ApiService.application.useProcessDebtorSummary(app_key);

  // Loader while fetching
  if (intelligenceData.isPending) {
    return (
      <DashboardLayout>
        <PageTitle
          title="Debtor Summary"
          pageTitle={[
            { title: `Applications`, link: "/dashboard/applications" },
            {
              title:
                fullApplication.data?.data?.application?.client_details?.name ??
                fullApplication.data?.application?.meta?.client_name ??
                "Application Item",
              link: `/dashboard/application/${app_key}`,
            },
            { title: "Debtor Summary" },
          ]}
        />
        <div className="flex flex-col gap-2 py-8 px-5 items-center">{loader}</div>
      </DashboardLayout>
    );
  }

  // If no data found
  if (!intelligenceData.data?.result || Object.keys(intelligenceData.data.result).length === 0) {
    return (
      <DashboardLayout
        headerRight={
          <Button
            loading={getDebtorSummary.isPending}
            disabled={getDebtorSummary.data && getDebtorSummary.isPending}
            onClick={() => {
              getDebtorSummary.mutate(
                { app_key: app_key!, type: "mca_statistics" },
                {
                  onSuccess: () => {
                    message.success("Processing has started. Refresh to check.");
                  },
                },
              );
            }}
            type="primary"
          >
            Generate Debtor Summary
          </Button>
        }
      >
        <PageTitle
          title="Debtor Summary"
          pageTitle={[
            { title: `Applications`, link: "/dashboard/applications" },
            {
              title:
                fullApplication.data?.data?.application?.client_details?.name ??
                fullApplication.data?.application?.meta?.client_name ??
                "Application Item",
              link: `/dashboard/application/${app_key}`,
            },
            { title: "Debtor Summary" },
          ]}
        />
        <div className="flex flex-col gap-2 py-8 px-5 items-center">
          {Empty.PRESENTED_IMAGE_SIMPLE}
          <Text type="secondary" className="text-center text-gray-500">
            No data found
          </Text>
        </div>
      </DashboardLayout>
    );
  }

  const intelligence = intelligenceData.data?.result;

  return (
    <DashboardLayout>
      <PageTitle
        title="Debtor Summary"
        pageTitle={[
          { title: `Applications`, link: "/dashboard/applications" },
          { title: "Application Item", link: `/dashboard/application/${app_key}` },
          { title: "Debtor Summary" },
        ]}
      />

      <div className="px-6">
        {/* Header Section */}
        <div className="flex justify-between items-center !mb-0 pt-6 border-b border-gray-200">
          <Title level={2} className="!m-0 flex items-center text-gray-800">
            Risk Analytics Dashboard
          </Title>
        </div>

        {/* Overview Section */}
        <OverView data={intelligence} />

        {/* Behavioral Analysis Section */}
        <div className="w-full overflow-hidden py-8">
          <h2 className="py-4 px-4 text-xl font-semibold text-gray-700">Behavioral Analysis</h2>
          <BehavioralCharts data={intelligence.behavioral_analysis} />
          {/* Legend for Behavioral Analysis */}
        </div>

        {/* Cash Flow Section */}
        <div className="w-full overflow-hidden py-8">
          <CashFlowPage data={intelligence} />
        </div>

        {/* Probability Default Analysis Section */}
        <div className="py-8">
          <ProbabilityDefaultAnalysis data={intelligence} />
          {/* Legend for Probability Default Analysis */}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewIntelligence;
